/*
 * Simple Image
 *
 * Supports:
 * - Retina
 * - Loading thumbnail
 * - Lazy loading of full resolution src
 * - Webp src for suitable browsers
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import {
    useLazyImage,
    useWebp
} from '$hooks/images';

const emptySrc = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const SimpleImage = (props) => {
    const imageRef = useRef();
    const webp = useWebp();

    const {
        alt,
        className,
        psrc,
        px2,
        role,
        src,
        style,
        x2
    } = props;

    const imageSrc = webp ? psrc : src;
    const imageX2 = webp ? px2 : x2;

    const {
        loading,
        loaded
    } = useLazyImage({
        imageRef,
        src: imageSrc
    });

    return (
        <img
            alt={alt}
            className={className}
            ref={imageRef}
            role={role}
            src={(loading || loaded) ? imageSrc : emptySrc}
            srcSet={(loading || loaded) && imageX2 ? `${imageX2} 2x` : null}
            style={{
                ...{
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity .3s'
                },
                ...style
            }} />
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    alt: PropTypes.string,
    className: PropTypes.string,
    psrc: PropTypes.string,
    px2: PropTypes.string,
    role: PropTypes.string,
    src: PropTypes.string.isRequired,
    style: PropTypes.object,
    x2: PropTypes.string
};

SimpleImage.propTypes = propTypes;

export default SimpleImage;
